import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Box, Button, Input, VStack, Stack, Textarea } from '@chakra-ui/react'
import { Field } from '@chakra-ui/react'
import { NativeSelectRoot, NativeSelectField } from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'
import { ChevronDown } from 'lucide-react'
import { unitSchema, type UnitFormData } from '../validation'
import type { Unit } from '../types'
import { useProperties } from '@/features/properties/hooks/useProperties'

interface UnitFormProps {
  unit?: Unit
  onSubmit: (data: UnitFormData) => void
  onCancel: () => void
  isLoading?: boolean
  showActions?: boolean
  formId?: string
  defaultResidenceId?: string
}

export function UnitForm({
  unit,
  onSubmit,
  onCancel,
  isLoading,
  showActions = true,
  formId,
  defaultResidenceId,
}: UnitFormProps) {
  const { t } = useTranslation()
  const { data: residences = [] } = useProperties()

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<UnitFormData>({
    resolver: zodResolver(unitSchema),
    defaultValues: unit
      ? {
          unit_number: unit.unit_number,
          unit_type: unit.unit_type ?? undefined,
          floor: unit.floor ?? undefined,
          stairwell: unit.stairwell ?? '',
          area_sqm: unit.area_sqm ?? undefined,
          general_shares: unit.general_shares || 0,
          work_fund_share: unit.work_fund_share ?? undefined,
          notes: unit.notes ?? '',
          residence_id: unit.residence_id,
        }
      : defaultResidenceId
      ? { residence_id: defaultResidenceId, general_shares: 0 }
      : { general_shares: 0 },
  })

  return (
    <Box as="form" onSubmit={handleSubmit(onSubmit)} id={formId}>
      <VStack gap={4} align="stretch">
        <Field.Root invalid={!!errors.residence_id} required>
          <Field.Label>{t('units.fields.residence')}</Field.Label>
          <NativeSelectRoot position="relative">
            <NativeSelectField {...register('residence_id')} paddingRight="10">
              <option value="" disabled>
                {t('units.select_residence')}
              </option>
              {residences.map((residence) => (
                <option key={residence.id} value={residence.id}>
                  {residence.name}
                </option>
              ))}
            </NativeSelectField>
            <Box
              position="absolute"
              right="3"
              top="50%"
              transform="translateY(-50%)"
              pointerEvents="none"
              color="gray.500"
            >
              <ChevronDown size={16} />
            </Box>
          </NativeSelectRoot>
          {errors.residence_id && (
            <Field.ErrorText>{errors.residence_id.message}</Field.ErrorText>
          )}
        </Field.Root>

        <Stack direction={{ base: 'column', md: 'row' }} gap={4}>
          <Field.Root invalid={!!errors.unit_number} flex={1} required>
            <Field.Label>{t('units.fields.unit_number')}</Field.Label>
            <Input
              {...register('unit_number')}
              placeholder={t('units.fields.unit_number')}
            />
            {errors.unit_number && (
              <Field.ErrorText>{errors.unit_number.message}</Field.ErrorText>
            )}
          </Field.Root>

          <Field.Root invalid={!!errors.unit_type} flex={1} required>
            <Field.Label>{t('units.fields.unit_type')}</Field.Label>
            <NativeSelectRoot position="relative">
              <NativeSelectField {...register('unit_type')} paddingRight="10">
                <option value="" disabled>
                  {t('units.select_type')}
                </option>
                <option value="RESIDENTIAL">{t('units.unit_types.RESIDENTIAL')}</option>
                <option value="PARKING">{t('units.unit_types.PARKING')}</option>
                <option value="CELLAR">{t('units.unit_types.CELLAR')}</option>
                <option value="COMMERCIAL">{t('units.unit_types.COMMERCIAL')}</option>
              </NativeSelectField>
              <Box
                position="absolute"
                right="3"
                top="50%"
                transform="translateY(-50%)"
                pointerEvents="none"
                color="gray.500"
              >
                <ChevronDown size={16} />
              </Box>
            </NativeSelectRoot>
            {errors.unit_type && (
              <Field.ErrorText>{errors.unit_type.message}</Field.ErrorText>
            )}
          </Field.Root>
        </Stack>

        <Stack direction={{ base: 'column', md: 'row' }} gap={4}>
          <Field.Root invalid={!!errors.floor} flex={1}>
            <Field.Label>{t('units.fields.floor')}</Field.Label>
            <Controller
              name="floor"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  type="number"
                  placeholder={t('units.fields.floor')}
                  value={field.value ?? ''}
                  onChange={(e) =>
                    field.onChange(e.target.value === '' ? null : parseInt(e.target.value, 10))
                  }
                />
              )}
            />
            {errors.floor && <Field.ErrorText>{errors.floor.message}</Field.ErrorText>}
          </Field.Root>

          <Field.Root invalid={!!errors.stairwell} flex={1}>
            <Field.Label>{t('units.fields.stairwell')}</Field.Label>
            <Input {...register('stairwell')} placeholder={t('units.fields.stairwell')} />
            {errors.stairwell && (
              <Field.ErrorText>{errors.stairwell.message}</Field.ErrorText>
            )}
          </Field.Root>
        </Stack>

        <Field.Root invalid={!!errors.area_sqm}>
          <Field.Label>{t('units.fields.area_sqm')}</Field.Label>
          <Controller
            name="area_sqm"
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                type="number"
                step="0.01"
                placeholder={t('units.fields.area_sqm')}
                value={field.value ?? ''}
                onChange={(e) =>
                  field.onChange(e.target.value === '' ? null : parseFloat(e.target.value))
                }
              />
            )}
          />
          {errors.area_sqm && <Field.ErrorText>{errors.area_sqm.message}</Field.ErrorText>}
        </Field.Root>

        <Stack direction={{ base: 'column', md: 'row' }} gap={4}>
          <Field.Root invalid={!!errors.general_shares} flex={1} required>
            <Field.Label>{t('units.fields.general_shares')}</Field.Label>
            <Controller
              name="general_shares"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  type="number"
                  placeholder={t('units.fields.general_shares')}
                  value={field.value ?? ''}
                  onChange={(e) =>
                    field.onChange(e.target.value === '' ? 0 : parseInt(e.target.value, 10))
                  }
                />
              )}
            />
            {errors.general_shares && (
              <Field.ErrorText>{errors.general_shares.message}</Field.ErrorText>
            )}
          </Field.Root>

          <Field.Root invalid={!!errors.work_fund_share} flex={1}>
            <Field.Label>{t('units.fields.work_fund_share')}</Field.Label>
            <Controller
              name="work_fund_share"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  type="number"
                  placeholder={t('units.fields.work_fund_share')}
                  value={field.value ?? ''}
                  onChange={(e) =>
                    field.onChange(e.target.value === '' ? null : parseInt(e.target.value, 10))
                  }
                />
              )}
            />
            {errors.work_fund_share && (
              <Field.ErrorText>{errors.work_fund_share.message}</Field.ErrorText>
            )}
          </Field.Root>
        </Stack>

        <Field.Root invalid={!!errors.notes}>
          <Field.Label>{t('units.fields.notes')}</Field.Label>
          <Textarea {...register('notes')} placeholder={t('units.fields.notes')} rows={3} />
          {errors.notes && <Field.ErrorText>{errors.notes.message}</Field.ErrorText>}
        </Field.Root>

        {showActions && (
          <Box display="flex" gap={3} justifyContent="flex-end" mt={4}>
            <Button variant="outline" onClick={onCancel} disabled={isLoading}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" colorPalette="brand" loading={isLoading}>
              {t('common.save')}
            </Button>
          </Box>
        )}
      </VStack>
    </Box>
  )
}
